import {
  type SubscriptionPeriodRepository,
  type SubscriptionPeriod,
} from "../interfaces";
import { supabase } from "@/integrations/supabase/supabaseClient";

export class SupabaseSubscriptionPeriodRepository
  implements SubscriptionPeriodRepository
{
  private async getCurrentUserId(): Promise<string> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");
    return user.id;
  }

  async getAll(): Promise<SubscriptionPeriod[]> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { data, error } = await supabase
      .from("subscription_periods")
      .select(
        `
        *,
        subscriptions!inner(user_id)
      `,
      )
      .order("start_date", { ascending: false });

    if (error) throw error;
    return data.map((item) => ({
      id: item.id,
      subscription_id: item.subscription_id,
      start_date: item.start_date,
      end_date: item.end_date,
      total_payments: item.total_payments,
      total_amount: item.total_amount,
      average_monthly_amount: item.average_monthly_amount,
      status: item.status as "active" | "completed" | "cancelled",
      created_at: item.created_at,
      updated_at: item.updated_at,
    }));
  }

  async getBySubscriptionId(
    subscriptionId: string,
  ): Promise<SubscriptionPeriod[]> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { data, error } = await supabase
      .from("subscription_periods")
      .select("*")
      .eq("subscription_id", subscriptionId)
      .order("start_date", { ascending: false });

    if (error) throw error;
    return data.map((item) => ({
      ...item,
      status: item.status as "active" | "completed" | "cancelled",
    }));
  }

  async create(
    period: Omit<SubscriptionPeriod, "id" | "created_at" | "updated_at">,
  ): Promise<SubscriptionPeriod> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { data, error } = await supabase
      .from("subscription_periods")
      .insert(period)
      .select()
      .single();

    if (error) throw error;
    return {
      ...data,
      status: data.status as "active" | "completed" | "cancelled",
    };
  }

  async update(
    id: string,
    updates: Partial<SubscriptionPeriod>,
  ): Promise<SubscriptionPeriod> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { data, error } = await supabase
      .from("subscription_periods")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return {
      ...data,
      status: data.status as "active" | "completed" | "cancelled",
    };
  }

  async delete(id: string): Promise<void> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { error } = await supabase
      .from("subscription_periods")
      .delete()
      .eq("id", id);

    if (error) throw error;
  }

  async bulkCreate(
    periods: Omit<SubscriptionPeriod, "id" | "created_at" | "updated_at">[],
  ): Promise<SubscriptionPeriod[]> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { data, error } = await supabase
      .from("subscription_periods")
      .insert(periods)
      .select();

    if (error) throw error;
    return data.map((item) => ({
      ...item,
      status: item.status as "active" | "completed" | "cancelled",
    }));
  }

  async deleteAll(): Promise<void> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("subscription_periods")
      .delete()
      .in(
        "subscription_id",
        (
          await supabase
            .from("subscriptions")
            .select("id")
            .eq("user_id", userId)
        ).data?.map((s) => s.id) || [],
      );

    if (error) throw error;
  }
}
